import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Grid,
  Alert,
  CircularProgress,
  Box,
  Typography,
  Chip,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';
import { roomsApi, reservationsApi } from '../services/api';
import { format } from 'date-fns';

interface RoomDetailDialogProps {
  open: boolean;
  roomId: number | null;
  onClose: () => void;
  onSuccess: () => void;
}

interface RoomDetail {
  id: number;
  room_number: string;
  floor: number | null;
  status: string;
  room_type: {
    id: number;
    name: string;
    base_rate: number;
  } | null;
}

const RoomDetailDialog: React.FC<RoomDetailDialogProps> = ({
  open,
  roomId,
  onClose,
  onSuccess,
}) => {
  const [room, setRoom] = useState<RoomDetail | null>(null);
  const [reservations, setReservations] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [editData, setEditData] = useState<any>({});

  useEffect(() => {
    if (open && roomId) {
      fetchRoom();
      fetchReservations();
    }
  }, [open, roomId]);

  const fetchRoom = async () => {
    if (!roomId) return;
    try {
      setLoading(true);
      const response = await roomsApi.getById(roomId);
      setRoom(response.data.data);
      setEditData({
        room_number: response.data.data.room_number,
        floor: response.data.data.floor || '',
        status: response.data.data.status,
      });
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load room');
    } finally {
      setLoading(false);
    }
  };

  const fetchReservations = async () => {
    if (!roomId) return;
    try {
      const response = await reservationsApi.getAll({ room_id: roomId });
      setReservations(response.data.data || []);
    } catch (err) {
      console.error('Failed to load reservations:', err);
    }
  };

  const handleUpdate = async () => {
    if (!roomId) return;

    try {
      setProcessing(true);
      setError(null);
      await roomsApi.update(roomId, editData);
      setEditing(false);
      fetchRoom();
      onSuccess();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update room');
    } finally {
      setProcessing(false);
    }
  };

  const handleStatusChange = async (newStatus: string) => {
    if (!roomId) return;

    try {
      setProcessing(true);
      setError(null);
      await roomsApi.updateStatus(roomId, newStatus);
      fetchRoom();
      onSuccess();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update status');
    } finally {
      setProcessing(false);
    }
  };

  const getStatusColor = (status: string) => {
    const colors: { [key: string]: 'success' | 'warning' | 'error' | 'info' | 'default' } = {
      available: 'success',
      occupied: 'error',
      reserved: 'warning',
      maintenance: 'info',
      out_of_order: 'default',
    };
    return colors[status] || 'default';
  };

  if (loading) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogContent>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <CircularProgress />
          </Box>
        </DialogContent>
      </Dialog>
    );
  }

  if (!room) {
    return null;
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Room {room.room_number} Details</Typography>
          <Chip
            label={room.status.replace('_', ' ').toUpperCase()}
            color={getStatusColor(room.status)}
            size="small"
          />
        </Box>
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Grid container spacing={2} sx={{ mt: 1 }}>
          {/* Room Information */}
          <Grid item xs={12}>
            <Card variant="outlined">
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="h6">Room Information</Typography>
                  {!editing && (
                    <Button size="small" onClick={() => setEditing(true)}>
                      Edit
                    </Button>
                  )}
                </Box>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="textSecondary">Room Number</Typography>
                    {editing ? (
                      <TextField
                        fullWidth
                        size="small"
                        value={editData.room_number}
                        onChange={(e) => setEditData({ ...editData, room_number: e.target.value })}
                        margin="dense"
                      />
                    ) : (
                      <Typography variant="body1">{room.room_number}</Typography>
                    )}
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="textSecondary">Floor</Typography>
                    {editing ? (
                      <TextField
                        fullWidth
                        type="number"
                        size="small"
                        value={editData.floor}
                        onChange={(e) => setEditData({ ...editData, floor: e.target.value ? parseInt(e.target.value) : null })}
                        margin="dense"
                      />
                    ) : (
                      <Typography variant="body1">{room.floor || 'N/A'}</Typography>
                    )}
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="textSecondary">Room Type</Typography>
                    <Typography variant="body1">
                      {room.room_type ? `${room.room_type.name} - $${room.room_type.base_rate}/night` : 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="textSecondary">Status</Typography>
                    {editing ? (
                      <TextField
                        fullWidth
                        select
                        size="small"
                        value={editData.status}
                        onChange={(e) => setEditData({ ...editData, status: e.target.value })}
                        margin="dense"
                      >
                        <MenuItem value="available">Available</MenuItem>
                        <MenuItem value="occupied">Occupied</MenuItem>
                        <MenuItem value="reserved">Reserved</MenuItem>
                        <MenuItem value="maintenance">Maintenance</MenuItem>
                        <MenuItem value="out_of_order">Out of Order</MenuItem>
                      </TextField>
                    ) : (
                      <Box>
                        <Chip
                          label={room.status.replace('_', ' ')}
                          color={getStatusColor(room.status)}
                          size="small"
                        />
                      </Box>
                    )}
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Quick Status Actions */}
          {!editing && (
            <Grid item xs={12}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom>Quick Status Change</Typography>
                  <Box display="flex" gap={1} flexWrap="wrap">
                    {room.status !== 'available' && (
                      <Button
                        size="small"
                        variant="outlined"
                        color="success"
                        onClick={() => handleStatusChange('available')}
                        disabled={processing}
                      >
                        Mark Available
                      </Button>
                    )}
                    {room.status !== 'occupied' && (
                      <Button
                        size="small"
                        variant="outlined"
                        color="error"
                        onClick={() => handleStatusChange('occupied')}
                        disabled={processing}
                      >
                        Mark Occupied
                      </Button>
                    )}
                    {room.status !== 'maintenance' && (
                      <Button
                        size="small"
                        variant="outlined"
                        color="info"
                        onClick={() => handleStatusChange('maintenance')}
                        disabled={processing}
                      >
                        Mark Maintenance
                      </Button>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          )}

          {/* Reservation History */}
          <Grid item xs={12}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" gutterBottom>Reservation History</Typography>
                {reservations.length === 0 ? (
                  <Typography variant="body2" color="textSecondary">
                    No reservations found for this room.
                  </Typography>
                ) : (
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Guest</TableCell>
                          <TableCell>Check-In</TableCell>
                          <TableCell>Check-Out</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell>Amount</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {reservations.map((reservation) => (
                          <TableRow key={reservation.id}>
                            <TableCell>{reservation.guest.name}</TableCell>
                            <TableCell>
                              {format(new Date(reservation.check_in_date), 'MMM dd, yyyy')}
                            </TableCell>
                            <TableCell>
                              {format(new Date(reservation.check_out_date), 'MMM dd, yyyy')}
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={reservation.status.replace('_', ' ')}
                                color={getStatusColor(reservation.status)}
                                size="small"
                              />
                            </TableCell>
                            <TableCell>${reservation.total_amount.toFixed(2)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        {editing ? (
          <>
            <Button onClick={() => { setEditing(false); fetchRoom(); }} disabled={processing}>
              Cancel
            </Button>
            <Button onClick={handleUpdate} variant="contained" disabled={processing}>
              {processing ? <CircularProgress size={24} /> : 'Save Changes'}
            </Button>
          </>
        ) : (
          <Button onClick={onClose}>Close</Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default RoomDetailDialog;

